'use client';

import { c as _c } from "react/compiler-runtime";
import type { PrV2ReviewResponse, PrV2StackPullRequest, PrV2StackResponse } from '@/lib/pr-v2/types';
import type { QueryClient } from '@tanstack/react-query';
import { useQueries, useQuery } from '@tanstack/react-query';
import { fetchPrV2Stack } from './fetch-pr-v2';
import { PR_V2_STANDARD_STALE_TIME_MS } from './query-config';
import { prV2StackQueryKey } from './query-keys';
import { prV2ReviewsQueryOptions } from './use-pr-v2-reviews';
import { getPrV2ReviewDecision } from '@/lib/pr-v2/reviewers';
import { asRecord } from '@/lib/pr-v2/utils/as-record';
import { ReviewerState } from '@/types/github';
const OPTIMISTIC_REVIEW_RETENTION_MS = 30_000;
function withOptimisticReviews(pullRequest: PrV2StackPullRequest, reviews: PrV2ReviewResponse[]): PrV2StackPullRequest {
  const now = Date.now();
  const persistedReviews = reviews.filter(review => asRecord(review.raw)?.is_optimistic !== true);
  const optimisticReviews = reviews.filter(review => asRecord(review.raw)?.is_optimistic === true && review.submittedAt !== null && now - Date.parse(review.submittedAt) <= OPTIMISTIC_REVIEW_RETENTION_MS);
  if (optimisticReviews.length === 0) {
    return pullRequest;
  }
  const reviewedLogins = new Set(optimisticReviews.map(review => review.userLogin?.toLowerCase()));
  const hasDecision = optimisticReviews.some(review => review.state !== ReviewerState.COMMENTED);
  // Match the server's per-reviewer decision: one approval must not hide someone else's change
  // request. Without review history, keep an existing blocker until the server can confirm it.
  const reviewDecision = hasDecision ? getPrV2ReviewDecision([...persistedReviews, ...optimisticReviews]) : pullRequest.reviewDecision;
  return {
    ...pullRequest,
    reviewDecision: persistedReviews.length === 0 && pullRequest.reviewDecision === 'changes_requested' && reviewDecision === 'approved' ? pullRequest.reviewDecision : reviewDecision,
    requestedReviewerLogins: pullRequest.requestedReviewerLogins.filter(login => !reviewedLogins.has(login.toLowerCase()))
  };
}
export function usePrV2Stack(owner, repo, number, options) {
  const $ = _c(12);
  let t0;
  if ($[0] !== number || $[1] !== owner || $[2] !== repo) {
    t0 = prV2StackQueryKey(owner, repo, number);
    $[0] = number;
    $[1] = owner;
    $[2] = repo;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== number || $[5] !== owner || $[6] !== repo) {
    t1 = t2 => {
      const {
        signal
      } = t2;
      return fetchPrV2Stack(owner, repo, number, signal);
    };
    $[4] = number;
    $[5] = owner;
    $[6] = repo;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const t2 = Boolean(owner && repo && number) && (options?.enabled ?? true);
  let t3;
  if ($[8] !== t0 || $[9] !== t1 || $[10] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      staleTime: PR_V2_STANDARD_STALE_TIME_MS,
      refetchOnWindowFocus: false
    };
    $[8] = t0;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return useQuery(t3);
}
export function usePrV2StackReviews(owner, repo, pullRequests) {
  const $ = _c(14);
  let t0;
  if ($[0] !== owner || $[1] !== pullRequests || $[2] !== repo) {
    let t1;
    if ($[4] !== owner || $[5] !== repo) {
      t1 = pullRequest => ({
        ...prV2ReviewsQueryOptions(owner, repo, String(pullRequest.number)),
        enabled: false,
        select: reviews => withOptimisticReviews(pullRequest, reviews)
      });
      $[4] = owner;
      $[5] = repo;
      $[6] = t1;
    } else {
      t1 = $[6];
    }
    t0 = pullRequests.map(t1);
    $[0] = owner;
    $[1] = pullRequests;
    $[2] = repo;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[7] !== t0) {
    t1 = {
      queries: t0
    };
    $[7] = t0;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const reviewQueries = useQueries(t1);
  let t2;
  if ($[9] !== pullRequests || $[10] !== reviewQueries) {
    let t3;
    if ($[12] !== reviewQueries) {
      t3 = (pullRequest_0, index) => [pullRequest_0.number, reviewQueries[index]?.data ?? pullRequest_0];
      $[12] = reviewQueries;
      $[13] = t3;
    } else {
      t3 = $[13];
    }
    t2 = new Map(pullRequests.map(t3));
    $[9] = pullRequests;
    $[10] = reviewQueries;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
}

/**
 * A PR with no cached stack, or a cached non-stack response, would lose the stack rail and its keys
 * until its stack loads. Its stack has the same PRs as the one on screen; updatedAt 0 makes that
 * page refetch. Does nothing when the PR already has a cached stack.
 */
export function seedPrV2Stack(queryClient: QueryClient, owner: string, repo: string, number: string, stack: PrV2StackResponse | undefined) {
  const queryKey = prV2StackQueryKey(owner, repo, number);
  if (stack && !queryClient.getQueryData<PrV2StackResponse>(queryKey)?.isPartOfStack) {
    queryClient.setQueryData(queryKey, stack, {
      updatedAt: 0
    });
  }
}